// Serviço de autenticação usando Supabase Auth
import { supabase } from '../lib/supabase';
import type { AuthSession } from '@supabase/supabase-js';

// Interface para dados de login
export interface LoginData {
  email: string;
  password: string;
}

// Interface para dados de registro
export interface SignUpData {
  email: string;
  password: string;
  nome?: string;
}

// Interface para usuário autenticado
export interface User {
  id: string;
  email: string;
  nome?: string;
  avatar?: string;
}

// Fazer login
export async function login(email: string, password: string) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;

    return {
      user: data.user,
      session: data.session,
    };
  } catch (error: any) {
    console.error('Erro ao fazer login:', error);
    throw new Error(error.message || 'Erro ao fazer login');
  }
}

// Fazer logout
export async function logout() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  } catch (error: any) {
    console.error('Erro ao fazer logout:', error);
    throw new Error(error.message || 'Erro ao fazer logout');
  }
}

// Registrar novo usuário
export async function signUp(email: string, password: string, nome?: string) {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          nome: nome || '',
        },
      },
    });

    if (error) throw error;

    return {
      user: data.user,
      session: data.session,
    };
  } catch (error: any) {
    console.error('Erro ao registrar usuário:', error);
    throw new Error(error.message || 'Erro ao registrar usuário');
  }
}

// Obter usuário atual
export async function getCurrentUser() {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error) throw error;
    
    if (!user) return null;

    return {
      id: user.id,
      email: user.email || '',
      nome: user.user_metadata?.nome || '',
      avatar: user.user_metadata?.avatar || undefined,
    } as User;
  } catch (error: any) {
    console.error('Erro ao obter usuário atual:', error);
    return null;
  }
}

// Obter sessão atual
export async function getCurrentSession(): Promise<AuthSession | null> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) throw error;
    
    return session;
  } catch (error: any) {
    console.error('Erro ao obter sessão:', error);
    return null;
  }
}

// Verificar se o usuário está autenticado
export async function isAuthenticated(): Promise<boolean> {
  try {
    const session = await getCurrentSession();
    return !!session;
  } catch (error) {
    return false;
  }
}

// Redefinir senha
export async function resetPassword(email: string) {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) throw error;
  } catch (error: any) {
    console.error('Erro ao redefinir senha:', error);
    throw new Error(error.message || 'Erro ao redefinir senha');
  }
}

// Atualizar senha
export async function updatePassword(newPassword: string) {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) throw error;
  } catch (error: any) {
    console.error('Erro ao atualizar senha:', error);
    throw new Error(error.message || 'Erro ao atualizar senha');
  }
}

// Observar mudanças na autenticação
export function onAuthStateChange(callback: (event: string, session: any) => void) {
  return supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });
}

